import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Send, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Why do I keep waking at 3 a.m.?",
  "Is this anxiety hormonal?",
  "How do I talk to my doctor about HRT?",
  "What actually helps with hot flashes?",
];

const SESSION_ID = "default";

export default function Companion() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [includeLogs, setIncludeLogs] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/chat/history?session_id=${SESSION_ID}`);
      setMessages(data || []);
    })();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: content, session_id: SESSION_ID, include_logs: includeLogs }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + payload.delta,
                };
                return copy;
              });
            }
            if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Something interrupted the thought. Please try again in a moment." };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I couldn't reach the companion just now. Please try again." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto rise">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold">
            Meridia Companion
          </p>
          <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="companion-title">
            Ask the 3 a.m. question.
          </h1>
          <p className="mt-3 text-[#52443E] max-w-2xl">
            Evidence-informed, calm, non-judgmental. Not a replacement for your clinician — a companion for everything in between.
          </p>
        </div>
      </div>

      <label className="mt-6 inline-flex items-center gap-3 card-paper px-4 py-3 cursor-pointer" data-testid="companion-include-logs">
        <input
          type="checkbox"
          checked={includeLogs}
          onChange={(e) => setIncludeLogs(e.target.checked)}
          className="w-4 h-4 accent-[#C26D53]"
          data-testid="companion-include-logs-checkbox"
        />
        <span className="text-sm text-[#52443E]">
          Share my recent 7-day check-ins for a more personal answer
        </span>
      </label>

      <div className="mt-6 card-paper p-5 sm:p-7 min-h-[320px]" data-testid="companion-messages">
        {messages.length === 0 ? (
          <div>
            <p className="text-[#73635B]">Try one of these to begin:</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  className="chip"
                  onClick={() => send(s)}
                  data-testid={`companion-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  data-testid={`companion-msg-${m.role}-${i}`}
                  className={
                    m.role === "user"
                      ? "max-w-[80%] bg-[#2C221E] text-[#FAF6F0] rounded-2xl rounded-br-md px-4 py-3"
                      : "max-w-[85%] bg-[#F4EDE2] text-[#2C221E] rounded-2xl rounded-bl-md px-5 py-4 border border-[#E5D9C8]"
                  }
                >
                  {m.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-[#8C7A70]">
                      <Sparkles size={12} /> Meridia
                    </div>
                  )}
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.content || <span className="opacity-60">thinking…</span>}
                  </div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {messages.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="companion-suggestions-persistent">
          <span className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold self-center mr-1">
            More to ask
          </span>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              className="chip"
              onClick={() => send(s)}
              disabled={sending}
              data-testid={`companion-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="mt-5 flex items-end gap-3"
      >
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Write what's on your mind…"
          className="input-paper flex-1 resize-none"
          data-testid="companion-input"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="btn-primary h-14 w-14 !p-0 inline-flex items-center justify-center"
          data-testid="companion-send-button"
          aria-label="Send"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
